import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Coffee, User, Sparkles, UtensilsCrossed, Wifi, Car, Flower2 } from 'lucide-react';

const Services = () => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1] }
  };

  const services = isEn ? [
    {
      icon: <Coffee size={48} />,
      name: 'Breakfast Included',
      subtitle: 'Every morning, an exceptional moment',
      description: 'Wake up to the soothing sound of the fountain and let yourself be guided to the rooftop where an authentic Moroccan breakfast awaits. Crispy msemmen, thousand-hole crepes (baghrir), freshly baked traditional bread, argan honey, homemade jam, artisan cheese, marinated olives, extra-virgin olive oil... Accompanied by fresh mint tea or strong coffee, this morning feast is prepared with love by our housekeeper.',
      image: '/images/gjkmwhbz_058F89B8-42D5-40E8-B6D7-CA0F6ABD5A4F.JPG'
    },
    {
      icon: <User size={48} />,
      name: 'Dedicated Housekeeper',
      subtitle: 'At your service with discretion',
      description: 'Our housekeeper is the discreet but essential soul of your stay. Present throughout the day, she ensures your absolute comfort. She prepares your meals, organizes your daily life, responds to your every request. Need recommendations? She knows Marrakech like the back of her hand. Her warm hospitality transforms your stay into an authentically Moroccan experience.',
      image: '/images/vwso2uf5_3991822D-CFC2-4845-9DBB-8175E45A614D.JPG'
    },
    {
      icon: <UtensilsCrossed size={48} />,
      name: 'Home-Cooked Dinners',
      subtitle: 'Moroccan gastronomy',
      description: 'On reservation, our housekeeper prepares traditional dishes that will delight your taste buds. The tajine, simmered for hours with selected spices, reveals complex flavors. The royal couscous consists of light semolina, tender vegetables and tender meats. These dinners are enjoyed in the intimacy of your patio or under the stars on the rooftop.',
      details: 'Tajine €20 • Royal Couscous €25 • Child Menu €15',
      image: '/images/rey0acg7_616FD1E8-33A4-4E04-A26C-D74C9EE4E5BC.JPG'
    },
    {
      icon: <Sparkles size={48} />,
      name: 'Daily Cleaning',
      subtitle: 'Impeccable cleanliness',
      description: 'Your riad remains spotless from the first to the last day. Our team intervenes daily with discretion. Rooms carefully made, sparkling bathrooms, perfectly maintained common areas, regularly changed linen. You return each evening to a perfectly tidy and welcoming space.',
      image: '/images/17wjim7n_17CCA6B1-4307-4C0A-B62A-6C7BDC845220.JPG'
    },
    {
      icon: <Flower2 size={48} />,
      name: 'Wellness Massages',
      subtitle: 'Absolute relaxation',
      description: 'Our massage room welcomes a professional therapist who masters traditional Moroccan techniques and contemporary massages. Argan oil massage, relaxing or energizing... Each treatment is personalized. The subdued atmosphere, delicate scents and soothing music create a cocoon of well-being.',
      image: '/images/ba0zqmeg_DC404373-6A69-4B8B-8B65-8E8AB7302FAE.JPG'
    },
    {
      icon: <Car size={48} />,
      name: 'Airport Transfers',
      subtitle: 'Serene arrival and departure',
      description: 'Our private transfer service welcomes you at the airport and ensures your return. A professional driver waits for you with a sign, helps you with your luggage and drives you comfortably to the riad. No more stress from taxis or getting lost in the medina.',
      image: '/images/ceb434ae64dfd9b6b617782923d8286dc344363cc5084758109fc8d8263f8dbc.png'
    },
    {
      icon: <Wifi size={48} />,
      name: 'High-Speed Wi-Fi',
      subtitle: 'Premium connectivity',
      description: 'Our high-speed Wi-Fi connection covers the entire riad. Whether you need to work remotely, share your moments on social networks or stay in touch with your loved ones, the connection is stable and fast. Modern luxury is also the freedom to choose between total disconnection and permanent connectivity.',
      image: '/images/2s55i11u_2ABB33CC-B1BB-4678-A614-5B16E824815A.JPG'
    }
  ] : [
    {
      icon: <Coffee size={48} />,
      name: 'Petit-Déjeuner Inclus',
      subtitle: 'Chaque matin, un moment d\'exception',
      description: 'Réveillez-vous au son apaisant de la fontaine et laissez-vous guider vers le rooftop où vous attend un petit-déjeuner marocain authentique. Msemmen croustillants, crêpes mille trous (baghrir), pain traditionnel fraîchement sorti du four, miel d\'argan, confiture maison, fromage artisanal, olives marinées, huile d\'olive extra-vierge... Accompagné de thé à la menthe fraîche ou de café fort, ce festin matinal est préparé avec amour par notre gouvernante.',
      image: '/images/gjkmwhbz_058F89B8-42D5-40E8-B6D7-CA0F6ABD5A4F.JPG'
    },
    {
      icon: <User size={48} />,
      name: 'Gouvernante Dévouée',
      subtitle: 'À votre service avec discrétion',
      description: 'Notre gouvernante est l\'âme discrète mais essentielle de votre séjour. Présente tout au long de la journée, elle veille à ce que votre confort soit absolu. Elle prépare vos repas, organise votre quotidien, répond à vos moindres demandes. Besoin de recommandations ? Elle connaît Marrakech comme sa poche. Son hospitalité chaleureuse transforme votre séjour en une expérience authentiquement marocaine.',
      image: '/images/vwso2uf5_3991822D-CFC2-4845-9DBB-8175E45A614D.JPG'
    },
    {
      icon: <UtensilsCrossed size={48} />,
      name: 'Dîners Maison',
      subtitle: 'La gastronomie marocaine',
      description: 'Sur réservation, notre gouvernante prépare des plats traditionnels qui raviront vos papilles. Le tajine, mijoté pendant des heures avec des épices sélectionnées, dévoile des saveurs complexes. Le couscous royal se compose de semoule légère, de légumes fondants et de viandes tendres. Ces dîners se dégustent dans l\'intimité de votre patio ou sous les étoiles du rooftop.',
      details: 'Tajine 20€ • Couscous Royal 25€ • Menu Enfant 15€',
      image: '/images/rey0acg7_616FD1E8-33A4-4E04-A26C-D74C9EE4E5BC.JPG'
    },
    {
      icon: <Sparkles size={48} />,
      name: 'Ménage Quotidien',
      subtitle: 'Propreté impeccable',
      description: 'Votre riad reste immaculé du premier au dernier jour. Notre équipe intervient quotidiennement avec discrétion. Chambres refaites avec soin, salles de bain étincelantes, espaces communs parfaitement entretenus, linge changé régulièrement. Vous rentrez chaque soir dans un espace parfaitement ordonné et accueillant.',
      image: '/images/17wjim7n_17CCA6B1-4307-4C0A-B62A-6C7BDC845220.JPG'
    },
    {
      icon: <Flower2 size={48} />,
      name: 'Massages Bien-Être',
      subtitle: 'Détente absolue',
      description: 'Notre salle de massage accueille une thérapeute professionnelle qui maîtrise les techniques traditionnelles marocaines et les massages contemporains. Massage aux huiles d\'argan, relaxant ou énergisant... Chaque soin est personnalisé. L\'atmosphère feutrée, les senteurs délicates et la musique apaisante créent un cocon de bien-être.',
      image: '/images/ba0zqmeg_DC404373-6A69-4B8B-8B65-8E8AB7302FAE.JPG'
    },
    {
      icon: <Car size={48} />,
      name: 'Transferts Aéroport',
      subtitle: 'Arrivée et départ sereins',
      description: 'Notre service de transfert privé vous accueille à l\'aéroport et assure votre retour. Un chauffeur professionnel vous attend avec une pancarte, vous aide avec vos bagages et vous conduit confortablement jusqu\'au riad. Fini le stress des taxis ou de se perdre dans la médina.',
      image: '/images/ceb434ae64dfd9b6b617782923d8286dc344363cc5084758109fc8d8263f8dbc.png'
    },
    {
      icon: <Wifi size={48} />,
      name: 'Wi-Fi Haut Débit',
      subtitle: 'Connectivité premium',
      description: 'Notre connexion Wi-Fi haut débit couvre l\'intégralité du riad. Que vous ayez besoin de travailler à distance, de partager vos moments sur les réseaux ou de rester en contact avec vos proches, la connexion est stable et rapide. Le luxe moderne, c\'est aussi la liberté de choisir entre déconnexion totale et connectivité permanente.',
      image: '/images/2s55i11u_2ABB33CC-B1BB-4678-A614-5B16E824815A.JPG'
    }
  ];

  return (
    <div data-testid="services-page" className="bg-[#FAF8F5] min-h-screen">
      {/* Hero */}
      <section className="pt-48 pb-32 px-8 md:px-16 lg:px-32">
        <div className="max-w-6xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[#B8860B] text-xs uppercase tracking-[0.3em] mb-8 text-center"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {isEn ? 'Excellence & Attention' : 'Excellence & Attention'}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-serif text-5xl md:text-7xl text-[#1A1A1A] mb-10 text-center"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}
          >
            {t('services.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-xl text-[#1A1A1A]/80 max-w-4xl mx-auto text-center leading-relaxed"
            style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}
          >
            {isEn 
              ? 'At Riad KAMA, service is not an option, it\'s a philosophy. Every detail has been designed to make your stay truly unforgettable.'
              : 'Au Riad KAMA, le service n\'est pas une option, c\'est une philosophie. Chaque détail a été pensé pour que votre séjour soit véritablement inoubliable.'}
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-32 px-8 md:px-16 lg:px-32">
        <div className="max-w-7xl mx-auto space-y-32">
          {services.map((service, index) => (
            <motion.div
              key={index}
              {...fadeInUp}
              transition={{ ...fadeInUp.transition, delay: index * 0.1 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-start ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className={`space-y-8 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="text-[#B8860B]">{service.icon}</div>
                <div>
                  <h3
                    className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-3"
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}
                  >
                    {service.name}
                  </h3>
                  <p
                    className="text-[#B8860B] text-base uppercase tracking-wider mb-6"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {service.subtitle}
                  </p>
                </div>
                <p
                  className="text-lg text-[#1A1A1A]/80 leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}
                >
                  {service.description}
                </p>
                {service.details && (
                  <div className="bg-white/60 backdrop-blur-sm border border-[#D4AF37]/30 p-6 shadow-sm">
                    <p className="text-base text-[#B8860B] font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {service.details}
                    </p>
                  </div>
                )}
              </div>

              <div className={`relative overflow-hidden h-[500px] border border-[#D4AF37]/30 group shadow-lg ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <img
                  src={service.image}
                  alt={service.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[2s]"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Services;
